import { BuildingAlternative } from '@/stores/SubprojectStore';
import {
  ElectricRateCard,
  EnergyKind,
  EnergyMinMax,
  ExternalEnergy,
  GasRateCard,
  OnsiteEnergy,
} from '../types';
import { normalizeToRating, normalizeToRatingLimit } from '../utils';

export function adaptElectric(card: ElectricRateCard): ExternalEnergy {
  return {
    id: `${card.utilityIdentifier}:electric:${card.state}:${card.sector}`,
    name: `${card.utilityName} — ${card.rateName}`,
    energyProvider: 'Electricity' as EnergyKind,
    tco2eMwh: card.tco2eMwh ? parseFloat(card.tco2eMwh) : 0,
    pctOfConventional: 0,
    costPerMWh: card.rateMwh ? parseFloat(card.rateMwh) : 0,
    notes: card.rateName,
  };
}

export function adaptGas(card: GasRateCard): ExternalEnergy {
  return {
    id: `${card.utilityIdentifier}:gas:${card.state}:${card.sector}`,
    name: `${card.utilityName} — ${card.rateName}`,
    energyProvider: 'Natural Gas' as EnergyKind,
    tco2eMwh: card.tco2eMwh ? parseFloat(card.tco2eMwh) : 0,
    pctOfConventional: 0,
    costPerMWh: card.rateMwh ? parseFloat(card.rateMwh) : 0,
    notes: card.rateName,
  };
}

export function isOnsiteEnergy(
  source: OnsiteEnergy | ExternalEnergy,
): source is OnsiteEnergy {
  return 'capitalCostUnit' in source;
}

export function isExternalEnergy(
  source: OnsiteEnergy | ExternalEnergy,
): source is ExternalEnergy {
  return 'energyProvider' in source;
}

export function computeOnsiteMinMax(
  onsite: OnsiteEnergy[],
): EnergyMinMax['onsite'] {
  const empty: EnergyMinMax['onsite'] = {
    tco2eMwh: { min: Infinity, max: -Infinity },
    annualEnergyProduced: { min: Infinity, max: -Infinity },
    capitalCostUnit: { min: Infinity, max: -Infinity },
  };

  if (onsite.length === 0) return empty;

  return onsite.reduce((a, b) => {
    a = {
      tco2eMwh: {
        min: Math.min(a.tco2eMwh.min, b.tco2eMwh ? Number(b.tco2eMwh) : 0),
        max: Math.max(a.tco2eMwh.max, b.tco2eMwh ? Number(b.tco2eMwh) : 0),
      },
      annualEnergyProduced: {
        min: Math.min(
          a.annualEnergyProduced.min,
          Number(b.annualEnergyProduced),
        ),
        max: Math.max(
          a.annualEnergyProduced.max,
          Number(b.annualEnergyProduced),
        ),
      },
      capitalCostUnit: {
        min: Math.min(a.capitalCostUnit.min, Number(b.capitalCostUnit)),
        max: Math.max(a.capitalCostUnit.max, Number(b.capitalCostUnit)),
      },
    };
    return a;
  }, empty);
}

export function computeExternalMinMax(
  external: ExternalEnergy[],
): EnergyMinMax['external'] {
  const empty: EnergyMinMax['external'] = {
    tco2eMwh: { min: Infinity, max: -Infinity },
    costPerMWh: { min: Infinity, max: -Infinity },
  };

  if (external.length === 0) return empty;

  return external.reduce((a, b) => {
    a = {
      tco2eMwh: {
        min: Math.min(a.tco2eMwh.min, b.tco2eMwh),
        max: Math.max(a.tco2eMwh.max, b.tco2eMwh),
      },
      costPerMWh: {
        min: Math.min(a.costPerMWh.min, b.costPerMWh),
        max: Math.max(a.costPerMWh.max, b.costPerMWh),
      },
    };
    return a;
  }, empty);
}

function getOnsiteEmissions(
  onsite: OnsiteEnergy[],
  alternative: BuildingAlternative,
): number {
  return (alternative?.onSiteEnergy?.params ?? []).reduce((acc, v) => {
    const onsiteUnit = onsite.find((e) => e.id === v.techId);
    if (!onsiteUnit) {
      console.error(`Unable to locate onsite unit with ID ${v.techId}`);
      return acc;
    }
    // NaN-safe parsing: guard against non-numeric strings from server
    const tco2e = Number(onsiteUnit.tco2eMwh);
    const safeTco2e = !onsiteUnit.tco2eMwh || isNaN(tco2e) ? 0 : tco2e;
    const produced = Number(onsiteUnit.annualEnergyProduced);
    const safeProduced = isNaN(produced) ? 0 : produced;
    return acc + safeTco2e * v.units * safeProduced;
  }, 0);
}

function getExternalEmissions(
  external: ExternalEnergy[],
  onsite: OnsiteEnergy[],
  alternative: BuildingAlternative,
): number {
  if (!alternative?.externalEnergy?.params) {
    return 0;
  }

  const onsiteSupply = (alternative?.onSiteEnergy?.params ?? []).reduce(
    (sum, p) => {
      const unit = onsite.find((e) => e.id === p.techId);
      if (!unit) return sum;
      return sum + Number(unit.annualEnergyProduced) * p.units;
    },
    0,
  );

  return alternative.externalEnergy.params.reduce((acc, v) => {
    // Look up by natural key (id on adapted ExternalEnergy objects is the natural key)
    const naturalKey = `${v.utilityIdentifier}:${v.ratePlanType}:${v.state}:${v.sector}`;
    const externalUnit = external.find((e) => e.id === naturalKey);
    if (!externalUnit) {
      return acc;
    }

    const grossMWh =
      alternative?.energyDemand?.[externalUnit.energyProvider as EnergyKind] ??
      0;
    const netMWh =
      externalUnit.energyProvider === 'Electricity'
        ? Math.max(0, grossMWh - onsiteSupply)
        : grossMWh;

    return acc + externalUnit.tco2eMwh * netMWh;
  }, 0);
}

/**
 * Annual emissions (tCO2e) for the energy selections on one alternative.
 *
 * Emissions only. The energy screen's COSTS -- capital and annual -- come from
 * the figures the backend computed and stored (see storedEnergyCosts in
 * ../utils.ts); a client-side sum missed the inflation ratio and the regional
 * multiplier the backend applies to on-site technology (decarb4-chain-activation
 * task 012), so it was removed rather than kept as a fallback.
 */
export function getAnnualEmissions(
  onsite: OnsiteEnergy[],
  external: ExternalEnergy[],
  alternative: BuildingAlternative,
): number {
  return (
    getOnsiteEmissions(onsite, alternative) +
    getExternalEmissions(external, onsite, alternative)
  );
}

function getPossibleUnitsForOnSiteEnergy(
  tech: OnsiteEnergy,
  demand: number,
  alternative: BuildingAlternative,
): number {
  // energy demand / energy generated

  // unit limit is based on conditioned space / floors (i.e, roof?), not demand
  if (tech.energySource == 'Solar') {
    const roofCoverageRatio = 0.8;
    const panelAreaSqft = 20;

    let conditionedSpace: number = 0;
    let floors: number = 1;
    let demandBased: number = 0;
    let spaceBased: number = 0;

    conditionedSpace = alternative.buildingParams.conditionedSpace.value || 0;
    floors = alternative?.buildingParams.numberOfFloors || 1;

    demandBased = Math.ceil(demand / Number(tech.annualEnergyProduced));
    spaceBased = Math.ceil(
      ((Number(conditionedSpace) / Number(floors)) * roofCoverageRatio) /
        panelAreaSqft,
    );

    return Math.min(demandBased, spaceBased);
  }

  return Math.ceil(demand / Number(tech.annualEnergyProduced));
}

// ---------------------------------------------------------------------------
// Shared helpers extracted from EnergyForBuildingDetails (Task 27)
// ---------------------------------------------------------------------------

interface NormalizedUnit {
  normalizedEnergy: number;
  normalizedCapital: number;
  limit: number;
}

/**
 * Normalise an energy unit's energy/capital ratings and compute the possible
 * unit limit.  Replaces 4x duplicated normalisation blocks in the original
 * EnergyForBuildingDetails component.
 */
export function normalizeEnergyUnit(
  unit: OnsiteEnergy | ExternalEnergy,
  minMax: EnergyMinMax,
  totalElectricityUsePerYear: number,
  alternative: BuildingAlternative,
): NormalizedUnit {
  const limit = !isExternalEnergy(unit)
    ? getPossibleUnitsForOnSiteEnergy(
        unit,
        totalElectricityUsePerYear,
        alternative,
      )
    : 0;

  const normalizedEnergy = isExternalEnergy(unit)
    ? normalizeToRating(
        minMax.external.tco2eMwh.min,
        minMax.external.tco2eMwh.max,
        unit.tco2eMwh,
      )
    : normalizeToRatingLimit(
        minMax.onsite.annualEnergyProduced.min,
        minMax.onsite.annualEnergyProduced.max,
        Number(unit.annualEnergyProduced),
        limit,
      );

  const normalizedCapital = isExternalEnergy(unit)
    ? normalizeToRating(
        minMax.external.costPerMWh.min,
        minMax.external.costPerMWh.max,
        unit.costPerMWh,
      )
    : normalizeToRatingLimit(
        minMax.onsite.capitalCostUnit.min,
        minMax.onsite.capitalCostUnit.max,
        Number(unit.capitalCostUnit),
        limit,
      );

  return { normalizedEnergy, normalizedCapital, limit };
}

/**
 * Generic filter/search/sort pipeline for energy unit arrays.
 * Replaces 3x duplicated filter chains in the original component.
 */
export function filterEnergyUnits<T extends OnsiteEnergy | ExternalEnergy>(
  units: T[],
  options: {
    excludeIds: Set<string>;
    searchString: string;
    energyFilter: EnergyKind[];
    energyProviderFilter?: EnergyKind[];
  },
): T[] {
  return units
    .filter((v) => !options.excludeIds.has(v.id))
    .filter((v) => {
      if (
        options.searchString.length > 0 &&
        !v.name.toLowerCase().includes(options.searchString.toLowerCase())
      ) {
        return false;
      }
      if (
        options.energyProviderFilter &&
        options.energyProviderFilter.length > 0
      ) {
        const provider = isExternalEnergy(v) ? v.energyProvider : '';
        if (!options.energyProviderFilter.includes(provider as EnergyKind)) {
          return false;
        }
      }
      if (
        options.energyFilter.length > 0 &&
        !isExternalEnergy(v) &&
        !options.energyFilter.includes(
          (v as OnsiteEnergy).energySource as EnergyKind,
        )
      ) {
        return false;
      }
      return true;
    })
    .sort((a, b) => Number(a.tco2eMwh) - Number(b.tco2eMwh));
}

interface DemandSection {
  key: string;
  value: number;
  displayName?: string;
}

/**
 * Build the demand-sections array (Electricity / Gas) from the alternative's
 * energy demand plus the selected providers.  Replaces the inline IIFE in the
 * original component.
 */
export function computeDemandSections(
  alternative: BuildingAlternative,
  onsiteProviders: OnsiteEnergy[],
  externalProviders: ExternalEnergy[],
): DemandSection[] {
  const gasDemand =
    (alternative.energyDemand['Natural Gas'] || 0) +
    (alternative.energyDemand['Propane'] || 0) +
    (alternative.energyDemand['Diesel'] || 0);

  const electricityDemand = alternative.energyDemand['Electricity'] || 0;

  const hasGasProviders = externalProviders.some((p) =>
    ['Natural Gas', 'Propane', 'Diesel'].includes(p.energyProvider),
  );

  const sections: DemandSection[] = [];
  if (electricityDemand > 0 || onsiteProviders.length > 0) {
    sections.push({ key: 'Electricity', value: electricityDemand });
  }
  if (gasDemand > 0 || hasGasProviders) {
    sections.push({ key: 'Natural Gas', value: gasDemand, displayName: 'Gas' });
  }
  return sections;
}
